<template>
    <div v-if="showOverlay" class="data-compliance-overlay fixed inset-0 z-[9999] flex items-end justify-end"
        style="background: rgba(0, 0, 0, 0.7)">
        <!-- Initial Modal -->
        <div v-if="!showPrivacyModal"
            class="data-compliance-modal privacy-modal m-2 p-4 md:m-4 md:p-8 rounded shadow-xl min-w-[320px] max-w-[98vw] md:max-w-[320px] min-h-[180px] text-left">
            <div class="border-b flex items-center justify-between pb-2 mb-4 text-black text-left">
                <h3 class="m-0 text-2xl font-bold">{{ t("privacy.welcome") }}</h3>
                <button type="button" class="ml-2 text-2xl leading-none opacity-70 hover:opacity-100 focus:outline-none"
                    aria-label="Close" @click="setCompliance(false)">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div class="text-gray-900 text-sm mb-6 text-left">
                <span class="font-bold">{{ t("privacy.cookiesTagline") }}</span>
                <span>, {{ t("privacy.improveExperienceTagline") }}&nbsp;</span>
                <span>
                    <a class="font-bold text-red-700 hover:underline"
                        href="/privacy" target="_blank">{{ t("privacy.policy")
                        }}</a>
                </span>
            </div>
            <div class="flex flex-row justify-end items-center gap-4 mt-6">
                <a href="#" class="font-bold text-red-700 hover:underline" @click.prevent="openPrivacyPreferences">
                    {{ t("privacy.managePreferences") }}
                </a>
                <button type="button"
                    class="inline-flex items-center rounded bg-red-700 px-8 py-2 text-white font-bold hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                    @click="acceptAllCookies">
                    {{ t("button.accept") }}
                </button>
            </div>
        </div>
        <!-- Privacy Choices Modal -->
        <div v-if="showPrivacyModal"
            class="privacy-modal p-2 md:p-6 rounded fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 min-w-[220px] max-w-[98vw] md:min-w-[680px] md:max-w-[900px] w-[98vw] md:w-[60vw] z-[10000] shadow-2xl text-left overflow-y-auto max-h-[98vh]">
            <div class="border-b mb-6 text-left">
                <div class="flex items-center justify-between pb-2 mb-3 text-black">
                    <h3 class="m-0 text-xl font-bold">
                        {{ t("privacy.yourChoices") }}
                    </h3>
                    <button type="button"
                        class="ml-2 text-2xl leading-none opacity-70 hover:opacity-100 focus:outline-none"
                        aria-label="Close" @click="closePrivacyPreferences">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <p class="text-gray-700 text-sm text-left">
                    {{ t("privacy.preferencesTagline") }}
                    <a class="font-bold text-red-700 hover:underline ml-1"
                        href="/privacy" target="_blank">{{ t("privacy.policy")
                        }}</a>
                </p>
            </div>
            <div>
                <div v-for="(section, idx) in privacySections" :key="section.key" class="mb-4 md:mb-6 text-left">
                    <div class="flex flex-wrap items-center justify-between cursor-pointer p-2 md:p-4 rounded bg-gray-100 border text-black"
                        @click="toggleSection(idx)">
                        <div class="flex items-center gap-2">
                            <i :class="[
                                section.expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line',
                                'text-xl md:text-2xl font-bold text-black',
                            ]"></i>
                            <span class="text-base md:text-lg font-bold">{{ section.title }}</span>
                        </div>
                        <div class="flex-1"></div>
                        <div class="ml-2">
                            <rh-toggle-switch :id="idx" :checked="section.enabled"
                                :disabled="section.key === 'necessary'"
                                @callBack="checked => onToggleSwitch(idx, checked)" />
                        </div>
                    </div>
                    <div v-if="section.expanded" class="mt-2 md:mt-3 pl-2 md:pl-3 text-gray-700 text-xs md:text-sm lg:text-base text-left">
                        {{ section.description }}
                    </div>
                </div>
                <div class="flex flex-col md:flex-row justify-end items-center gap-2 md:gap-4 mt-6 md:mt-8">
                    <a href="#" class="font-bold text-sm text-red-700 hover:underline" @click.prevent="acceptOnlyNecessary">
                        {{ t("privacy.acceptOnlyNecessaryCookies") }}
                    </a>
                    <button type="button"
                        class="inline-flex items-center rounded bg-red-700 px-6 py-2 text-white font-bold hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                        @click="setCompliance">
                        {{ t("privacy.acceptCookies") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";

import rhToggleSwitch from "./atoms/rh-toggle-switch.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const DATA_COMPLIANCE_KEY = "RH_DTA";

const showOverlay = ref(false);
const showPrivacyModal = ref(false);

const privacySections = reactive([
    {
        key: "necessary",
        title: t("privacy.strictlyNecessary.title"),
        enabled: true,
        expanded: false,
        description: t("privacy.strictlyNecessary.description"),
    },
    {
        key: "analytics",
        title: t("privacy.analytics.title"),
        enabled: true,
        expanded: false,
        description: t("privacy.analytics.description"),
    },
    {
        key: "marketing",
        title: t("privacy.marketing.title"),
        enabled: true,
        expanded: false,
        description: t("privacy.marketing.description"),
    },
]);

function setDataCompliance(data) {
    localStorage.setItem(DATA_COMPLIANCE_KEY, JSON.stringify(data));
}
function getDataCompliance() {
    const stored = localStorage.getItem(DATA_COMPLIANCE_KEY);
    return stored ? JSON.parse(stored) : null;
}
function removeDataCompliance() {
    localStorage.removeItem(DATA_COMPLIANCE_KEY);
}

function setCompliance() {
    const data = {};
    privacySections.forEach((section) => {
        data[section.key] = section.enabled;
    });
    data.timestamp = new Date().getTime();
    setDataCompliance(data);
    showOverlay.value = false;
    showPrivacyModal.value = false;
}

function acceptAllCookies() {
    privacySections.forEach((section) => {
        section.enabled = true;
    });
    setCompliance();
}

function acceptOnlyNecessary() {
    privacySections.forEach((section) => {
        section.enabled = section.key === "necessary";
    });
    setCompliance();
}

function checkCompliance() {
    const stored = getDataCompliance();
    let show = true;
    const now = new Date().getTime();
    if (stored && stored.timestamp) {
        const diff = now - stored.timestamp;
        const thirtyDays = 30 * 24 * 60 * 60 * 1000;
        show = diff > thirtyDays;
        if (show) {
            removeDataCompliance();
        }
    }
    showOverlay.value = show;
}

function toggleSection(idx) {
    privacySections.forEach((section, i) => {
        section.expanded = i === idx ? !section.expanded : false;
    });
}

function onToggleSwitch(idx, checked) {
    if (privacySections[idx].key !== "necessary") {
        privacySections[idx].enabled = checked;
    }
}

function openPrivacyPreferences() {
    const stored = getDataCompliance();
    if (stored) {
        privacySections.forEach((section) => {
            if (stored.hasOwnProperty(section.key)) {
                section.enabled = stored[section.key];
            }
        });
    }
    showOverlay.value = true;
    showPrivacyModal.value = true;
}

function closePrivacyPreferences() {
    showPrivacyModal.value = false;
    const stored = getDataCompliance();
    if (stored && stored.timestamp) {
        showOverlay.value = false;
    }
}

onMounted(() => {
    checkCompliance();
    // Expose openPrivacyPreferences globally so it can be called from Razor pages
    window.openPrivacyPreferences = openPrivacyPreferences;
});
</script>

<style>
/* Hide compliance modals when printing */
@media print {

    .data-compliance-overlay,
    .data-compliance-modal,
    .privacy-modal {
        display: none !important;
    }
}

.privacy-modal {
    background: #fff !important;
    color: #1a202c !important;
    padding: 1.5rem !important;
    border-radius: 0.5rem !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1) !important;
}

.privacy-modal h3 {
    font-weight: 700 !important;
    color: #000 !important;
    margin: 0 !important;
}

.privacy-modal p {
    color: #4b5563 !important;
    font-size: 0.875rem !important;
}

.privacy-modal .border-b {
    border-bottom: 1px solid #d1d1d1 !important;
    margin-bottom: 1.5rem !important;
}

.data-compliance-overlay {
    background-color: rgba(0, 0, 0, 0.7) !important;
}

.privacy-modal button[type="button"] {
    padding: 0.5rem 1.5rem !important;
    font-weight: 700 !important;
    font-size: 0.875rem !important;
    border-radius: 0.375rem !important;
    border: none !important;
    cursor: pointer !important;
}

.privacy-modal button.bg-red-700 {
    background-color: #b91c1c !important;
    color: #fff !important;
}

.privacy-modal button.bg-red-700:hover {
    background-color: #991b1b !important;
}

.privacy-modal .bg-gray-100 {
    background-color: #f3f4f6 !important;
    padding: 1rem !important;
    border: 1px solid #e5e7eb !important;
}

.privacy-modal a {
    color: #b91c1c !important;
    text-decoration: none !important;
}

.privacy-modal a:hover {
    text-decoration: underline !important;
}
</style>
